import z from 'zod';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle, Divider, Grid, Typography } from '@mui/material';
import {
  Button,
  SaveButton,
  SaveContextProvider,
  SaveHandler,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import { FieldValues, useFormContext } from 'react-hook-form';

import client from 'src/data/api';
import { CompanySubscriptionSetRecord } from 'src/types';
import { multilineIds, zodIssuesIntoErrorsMap } from 'src/utils/validation';
import { createDialogFormContext } from 'src/components/DialogFormContext';
import { useCompanySubscriptionSetContext } from '../../Context';

export const {
  provider: AddCompanySubscriptionsDialogContextProvider,
  useContext: useAddCompanySubscriptionsDialogContext,
} = createDialogFormContext();

const CustomToolbar = ({ onCancel }: { onCancel: () => void }) => {
  const { reset: resetForm } = useFormContext();

  return (
    <Toolbar>
      <Grid container justifyContent="space-between">
        <Button label="Cancel" onClick={onCancel} />
        <SaveButton
          variant="contained"
          type="button"
          mutationOptions={{
            onSuccess: () => resetForm(),
          }}
        />
      </Grid>
    </Toolbar>
  );
};

const validationSchema = z.object({ companyIds: multilineIds });

export const AddCompanySubscriptionsDialog = () => {
  const dialogCtx = useAddCompanySubscriptionsDialogContext();
  const { currentSubscriptionCounts } = useCompanySubscriptionSetContext();
  const companySubscriptionSetRecord = useRecordContext<CompanySubscriptionSetRecord>();
  const notify = useNotify();
  const refresh = useRefresh();

  const [createCompanySubscriptions] = useMutation<{
    insert_company_subscriptions: { returning: { id: string }[]; affected_rows: number };
  }>(
    gql`
      mutation insert_company_subscriptions($objects: [company_subscriptions_insert_input!]!) {
        insert_company_subscriptions(
          objects: $objects
          on_conflict: { constraint: company_subscriptions_ci_cssi_unique, update_columns: [] }
        ) {
          returning {
            id
          }
          affected_rows
        }
      }
    `,
    { client, errorPolicy: 'all' },
  );

  if (companySubscriptionSetRecord?.all_companies) {
    // "All Companies" sets don't have explicit company subscriptions, so this component shouldn't
    // be rendered
    return null;
  }

  const availableSubscriptionSlotCount =
    companySubscriptionSetRecord?.company_limit !== undefined && currentSubscriptionCounts !== undefined
      ? companySubscriptionSetRecord.company_limit - currentSubscriptionCounts.total
      : undefined;

  const handleSubmit: SaveHandler<FieldValues> = async (rawValues, callbacks) => {
    if (!companySubscriptionSetRecord) return undefined;

    if (availableSubscriptionSlotCount === undefined) {
      notify('There was a problem validating the number of available slots. Please try again.', {
        type: 'error',
        autoHideDuration: 8000,
      });
      return undefined;
    }

    const validationResult = validationSchema.safeParse(rawValues);
    if (!validationResult.success) return zodIssuesIntoErrorsMap(validationResult.error.issues);
    const values = validationResult.data;

    // dedupe
    const companyIdSet = new Set(values.companyIds);

    // check if number of entered company IDs exceeds the number of available slots
    if (companyIdSet.size > availableSubscriptionSlotCount) {
      return {
        companyIds: `${companyIdSet.size} unique IDs were entered, but only \
${availableSubscriptionSlotCount} slots are available`,
      };
    }

    const subscriptionsToInsert = [...companyIdSet].map((companyID) => ({
      company_id: companyID,
      company_subscription_set_id: companySubscriptionSetRecord.id,
    }));

    const { data, errors } = await createCompanySubscriptions({ variables: { objects: subscriptionsToInsert } });
    if (errors) {
      console.error('Error adding subscriptions:', errors);
      notify('There was a problem adding the Company Subscriptions. Please try again.', { type: 'error' });
      return undefined;
    }

    const createdCount = data?.insert_company_subscriptions.affected_rows ?? 0;

    if (createdCount) {
      notify(`${createdCount} Company Subscription${createdCount > 1 ? 's were' : ' was'} added successfully.`, {
        type: 'success',
      });
    } else {
      notify(`No subscriptions were added, because they already exist for the given company ID(s).`, {
        type: 'info',
      });
    }

    if (callbacks?.onSuccess) callbacks.onSuccess();
    if (dialogCtx.onSuccess) dialogCtx.onSuccess();
    refresh();
    dialogCtx.setClosed();
    return undefined;
  };

  return (
    <Dialog open={dialogCtx.open} onClose={dialogCtx.setClosed} fullWidth>
      <DialogTitle>Add Company Subscriptions</DialogTitle>
      <Divider />

      <DialogContent>
        <Typography textAlign="center" color="primary" fontWeight="600">
          Available subscription slots: {availableSubscriptionSlotCount}
        </Typography>
        <SaveContextProvider value={{ save: handleSubmit }}>
          <SimpleForm mode="onChange" toolbar={<CustomToolbar onCancel={dialogCtx.setClosed} />} mt={2} mx={1}>
            <TextInput
              label="Company ID(s)"
              source="companyIds"
              multiline
              validate={required()}
              fullWidth
              minRows={15}
              maxRows={15}
              autoFocus
            />
          </SimpleForm>
        </SaveContextProvider>
      </DialogContent>
    </Dialog>
  );
};
